import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import CSVButton from '../../components/Common/CSVButton';
import { exportToCSV } from '../../utils/csvExport';
import ViewDistributorDetails from '../../components/Organization/View Distribution/ViewDistributorDetails';
import OrderReport from '../../components/Organization/View Distribution/OrderReport';
import ActivitiesReport from '../../components/Organization/View Distribution/ActivitiesReport';
import CollectionReport from '../../components/Organization/View Distribution/CollectionReport';
import NewTarget from '../../components/Organization/View Distribution/NewTarget';
import ManageTarget from '../../components/Organization/View Distribution/ManageTarget';
import TargetAchievement from '../../components/Organization/View Distribution/TargetAchievement';
import SalesReport from '../../components/Organization/View Distribution/SalesReport';

interface Distributor {
    code: string;
    name: string;
    location?: string;
    zone?: string;
    areas?: string[];
}

interface ViewDistributorProps {
    distributor?: Distributor | null;
    onBack?: () => void;
}

const ViewDistributor: React.FC<ViewDistributorProps> = ({ distributor, onBack }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Distributor passed via props or router state
    const activeDist = distributor || location.state?.distributor || {};

    const handleBack = onBack || (() => navigate('/dashboard/organisation'));

    // Tabs logic
    const [activeTab, setActiveTab] = useState('Home');

    const tabs = [
        'Home', 
        // 'Orders', 'Sales Report', 'Activities Report', 
        // 'Collection Report', 'New Target', 'Manage Target',
        // 'Target Achievement', 'Sales Ledger', 'Credit Note', 
        // 'Stock At Outlet', 'Schemes', 'Outlet Activity Form'
    ];

    const handleExportCSV = () => {
        const headers = ['Distributor ID', 'Name', 'ERP Code', 'Phone', 'Zone', 'City', 'Status'];
        const rows = [
            [
                activeDist.id || '',
                activeDist.name || activeDist.companyName || '',
                activeDist.erpCode || activeDist.sapCode || '',
                activeDist.phone || '',
                activeDist.zone || '',
                activeDist.city || '',
                activeDist.status || 'Active',
            ]
        ];
        exportToCSV(`distributor_${activeDist.id || 'details'}`, headers, rows);
    };

    return (
        <div className="space-y-6">
            {/* Header row with Back button and Page Title */}
            <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBack}
                        className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
                        title="Go Back"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
                        {activeDist.name || activeDist.companyName || 'Distributor Details'}
                    </h1>
                </div>

                {/* Top Action buttons */}
                <div className="flex items-center gap-2.5">
                    <CSVButton onClick={handleExportCSV} />
                </div>
            </div>

            {/* Tabs Row matching mockup layout structure */}
            <div className="flex flex-wrap items-center gap-2.5 mb-6">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab || (tab === 'Orders' && activeTab === 'Order Report');
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-12 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all duration-150 ${
                                isActive
                                    ? 'bg-[#C62828] text-white shadow-sm'
                                    : 'bg-white border border-[#ECECF2] text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {tab}
                        </button>
                    );
                })}
            </div>

            {/* Main Info Card / Content Switcher based on activeTab */}
            {activeTab === 'Home' && (
                <ViewDistributorDetails activeDist={activeDist} />
            )}
            {(activeTab === 'Orders' || activeTab === 'Order Report') && (
                <OrderReport />
            )}
            {activeTab === 'Activities Report' && (
                <ActivitiesReport />
            )}
            {activeTab === 'Collection Report' && (
                <CollectionReport />
            )}
            {activeTab === 'New Target' && (
                <NewTarget />
            )}
            {activeTab === 'Manage Target' && (
                <ManageTarget />
            )}
            {activeTab === 'Target Achievement' && (
                <TargetAchievement />
            )}
            {activeTab === 'Sales Report' && (
                <SalesReport />
            )}
            {activeTab !== 'Home' && activeTab !== 'Orders' && activeTab !== 'Order Report' && activeTab !== 'Activities Report' && activeTab !== 'Collection Report' && activeTab !== 'New Target' && activeTab !== 'Manage Target' && activeTab !== 'Target Achievement' && activeTab !== 'Sales Report' && (
                <div className="bg-white rounded-3xl border border-[#ECECF2] p-4 text-center text-slate-500 text-xs">
                    {activeTab} View (Connected to API in next phase)
                </div>
            )}
        </div>
    );
};

export default ViewDistributor;